// src/components/festival/LiveStreamHub.tsx
// DROP 36 — Live Stream Hub | DROP 40F AUDIT CLEAN
import React, { useState, useEffect, useCallback } from 'react';
import {
  Radio, Settings, Plus, Trash2, Edit2,
  CheckCircle, AlertCircle,
  Eye, EyeOff, WifiOff,
} from 'lucide-react';

const C = {
  bg:'#070D0A', surface:'#0E1812', card:'#121E16', border:'#1C2E22',
  text:'#EDE8DC', muted:'rgba(237,232,220,0.55)', dim:'rgba(237,232,220,0.35)',
  green:'#0F5132', gold:'#C9A84C', red:'#ef4444', blue:'#60a5fa',
};

const inp: React.CSSProperties = {
  width:'100%', background:C.bg, border:`1px solid ${C.border}`,
  borderRadius:7, padding:'9px 12px', color:C.text, fontSize:13,
  outline:'none', boxSizing:'border-box',
};

interface StreamChannel {
  id: string;
  platform: 'youtube' | 'facebook' | 'custom';
  label: string;
  embedId: string;
  isLive: boolean;
  isEnabled: boolean;
  scheduledDate: string;
  description: string;
}

interface BroadcastSlot {
  id: string;
  date: string;
  time: string;
  title: string;
  venue: string;
  type: string;
  streamChannelId: string;
  isLive: boolean;
}

interface Props { currentUser?: any; }

const TOKEN = () =>
  localStorage.getItem('erue_auth_token') || sessionStorage.getItem('erue_auth_token') || '';

const DEFAULT_CHANNELS: StreamChannel[] = [
  {
    id: 'yt-main', platform: 'youtube',
    label: 'Main Stage — YouTube Live',
    embedId: '', isLive: false, isEnabled: true,
    scheduledDate: 'December 24, 2026 — Opening Ceremony',
    description: 'Main festival stage. Royal Opening Ceremony, cultural performances, Grand Finale.',
  },
  {
    id: 'fb-main', platform: 'facebook',
    label: 'Royal Updates — Facebook Live',
    embedId: '', isLive: false, isEnabled: true,
    scheduledDate: 'December 24–30, 2026 — Daily Updates',
    description: 'Daily festival updates on the official Uzairue Kingdom Facebook page.',
  },
];

const DEFAULT_SCHEDULE: BroadcastSlot[] = [
  { id:'s1', date:'Dec 24', time:'10:00 AM', title:'Grand Opening Ceremony',
    venue:'Palace of the Ogieneni', type:'ceremony', streamChannelId:'yt-main', isLive:false },
  { id:'s2', date:'Dec 25', time:'09:00 AM', title:'Cultural Heritage Day — Masquerade Parade',
    venue:'Festival Grounds', type:'cultural', streamChannelId:'yt-main', isLive:false },
  { id:'s3', date:'Dec 26', time:'06:00 PM', title:'Miss ERUE Pageant 2026',
    venue:'Main Stage', type:'pageant', streamChannelId:'yt-main', isLive:false },
  { id:'s4', date:'Dec 27', time:'02:00 PM', title:'Football Tournament Finals',
    venue:'Festival Ground', type:'sport', streamChannelId:'fb-main', isLive:false },
  { id:'s5', date:'Dec 28', time:'07:00 PM', title:'Ekaba Heritage Night — Iyora-Uzairue',
    venue:'Sacred Grove Arena', type:'heritage', streamChannelId:'yt-main', isLive:false },
  { id:'s6', date:'Dec 29', time:'06:00 PM', title:'Diaspora Night & Awards Ceremony',
    venue:'Main Stage', type:'ceremony', streamChannelId:'yt-main', isLive:false },
  { id:'s7', date:'Dec 30', time:'04:00 PM', title:'Grand Finale & Closing Ceremony',
    venue:'Palace of the Ogieneni', type:'ceremony', streamChannelId:'yt-main', isLive:false },
];

const TYPE_COLORS: Record<string, string> = {
  ceremony:      'rgba(201,168,76,0.15)',
  cultural:      'rgba(34,197,94,0.15)',
  pageant:       'rgba(236,72,153,0.15)',
  sport:         'rgba(59,130,246,0.15)',
  heritage:      'rgba(168,85,247,0.15)',
  community:     'rgba(249,115,22,0.15)',
};

const ytEmbedUrl = (embedId: string) => {
  if (!embedId) return '';
  const match = embedId.match(/(?:v=|youtu\.be\/)([^&\s]+)/);
  const id = match ? match[1] : embedId;
  return `https://www.youtube.com/embed/${id}?autoplay=0&rel=0`;
};

/* ═══════════════════════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════════════════════ */
const LiveStreamHub: React.FC<Props> = ({ currentUser }) => {
  const [channels,       setChannels]       = useState<StreamChannel[]>(DEFAULT_CHANNELS);
  const [schedule,       setSchedule]       = useState<BroadcastSlot[]>(DEFAULT_SCHEDULE);
  const [loading,        setLoading]        = useState(true);
  const [activeChannel,  setActiveChannel]  = useState<string>('yt-main');
  const [tab,            setTab]            = useState<'watch'|'schedule'|'admin'>('watch');
  const [msg,            setMsg]            = useState<{type:'ok'|'err';text:string}|null>(null);
  const [editChannel,    setEditChannel]    = useState<StreamChannel|null>(null);
  const [showSlotForm,   setShowSlotForm]   = useState(false);
  const [slotForm,       setSlotForm]       = useState({
    date:'', time:'', title:'', venue:'', type:'ceremony', streamChannelId:'yt-main',
  });
  const [saving, setSaving] = useState(false);

  const isAdmin = !!(currentUser && ['PLATFORM_ADMIN','MEDIA_MANAGER','FESTIVAL_COORDINATOR']
    .includes((currentUser.systemRole || currentUser.role || '').toUpperCase()));

  const flash = (type: 'ok'|'err', text: string) => {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 4000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch('/api/public/stream-config');
      const d = await r.json();
      if (d.success && d.channels?.length) setChannels(d.channels);
      if (d.success && d.schedule?.length) setSchedule(d.schedule);
    } catch { /* use defaults silently */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveChannels = async (updated: StreamChannel[]) => {
    setSaving(true);
    try {
      const r = await fetch('/api/admin/stream-config', {
        method: 'PUT',
        headers: { 'Content-Type':'application/json', Authorization:`Bearer ${TOKEN()}` },
        body: JSON.stringify({ channels: updated, schedule }),
      });
      const d = await r.json();
      if (d.success) { setChannels(updated); flash('ok', 'Stream config saved.'); }
      else flash('err', d.error || 'Save failed');
    } catch { flash('err', 'Network error'); }
    setSaving(false);
  };

  const toggleLive    = (chId: string) =>
    saveChannels(channels.map(c => c.id === chId ? { ...c, isLive: !c.isLive } : c));

  const toggleEnabled = (chId: string) =>
    saveChannels(channels.map(c => c.id === chId ? { ...c, isEnabled: !c.isEnabled } : c));

  const saveEditChannel = async () => {
    if (!editChannel) return;
    await saveChannels(channels.map(c => c.id === editChannel.id ? editChannel : c));
    setEditChannel(null);
  };

  const addSlot = async () => {
    if (!slotForm.title.trim()) { flash('err', 'Title required'); return; }
    const updated = [...schedule, { id:`s${Date.now()}`, ...slotForm, isLive:false }];
    setSaving(true);
    try {
      const r = await fetch('/api/admin/stream-config', {
        method: 'PUT',
        headers: { 'Content-Type':'application/json', Authorization:`Bearer ${TOKEN()}` },
        body: JSON.stringify({ channels, schedule: updated }),
      });
      const d = await r.json();
      if (d.success) {
        setSchedule(updated);
        setShowSlotForm(false);
        setSlotForm({ date:'', time:'', title:'', venue:'', type:'ceremony', streamChannelId:'yt-main' });
        flash('ok', 'Broadcast slot added.');
      } else flash('err', d.error || 'Save failed');
    } catch { flash('err', 'Network error'); }
    setSaving(false);
  };

  const removeSlot = async (id: string) => {
    const updated = schedule.filter(s => s.id !== id);
    try {
      await fetch('/api/admin/stream-config', {
        method: 'PUT',
        headers: { 'Content-Type':'application/json', Authorization:`Bearer ${TOKEN()}` },
        body: JSON.stringify({ channels, schedule: updated }),
      });
      setSchedule(updated);
      flash('ok', 'Slot removed.');
    } catch { flash('err', 'Remove failed'); }
  };

  const toggleSlotLive = (id: string) =>
    setSchedule(prev => prev.map(s => s.id === id ? { ...s, isLive: !s.isLive } : s));

  const ch       = channels.find(c => c.id === activeChannel);
  const liveCount = channels.filter(c => c.isLive && c.isEnabled).length;

  /* ── Loading State ──────────────────────────────────────────────────────── */
  if (loading) {
    return (
      <div style={{ minHeight:'60vh', display:'flex', alignItems:'center',
        justifyContent:'center', background:C.bg }}>
        <div style={{ textAlign:'center' }}>
          <Radio size={36} style={{ color:C.red, margin:'0 auto 16px', display:'block',
            animation:'pulse 1.5s ease-in-out infinite' }} />
          <p style={{ color:C.muted, fontSize:14 }}>Loading Live Stream Hub…</p>
        </div>
      </div>
    );
  }

  /* ── Main Render ─────────────────────────────────────────────────────────── */
  return (
    <div style={{ background:C.bg, minHeight:'100vh', padding:'24px 0' }}>
      <div style={{ maxWidth:1200, margin:'0 auto', padding:'0 20px' }}>

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div style={{ background:'linear-gradient(135deg,#060F09 0%,#0A2015 100%)',
          borderRadius:16, padding:'28px 32px', marginBottom:24,
          border:`1px solid rgba(201,168,76,0.2)` }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
            flexWrap:'wrap', gap:16 }}>
            <div>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                <div style={{ width:8, height:8, borderRadius:'50%',
                  background:liveCount > 0 ? C.red : 'rgba(239,68,68,0.3)',
                  boxShadow:liveCount > 0 ? '0 0 8px #ef4444' : 'none',
                  animation:liveCount > 0 ? 'blink 1.5s infinite' : 'none' }} />
                <span style={{ fontSize:10, fontWeight:700, letterSpacing:2,
                  textTransform:'uppercase', color:liveCount > 0 ? C.red : C.muted }}>
                  {liveCount > 0 ? `${liveCount} LIVE NOW` : 'OFF AIR'}
                </span>
              </div>
              <h1 style={{ fontFamily:'Cinzel,serif', fontSize:26, fontWeight:700,
                color:C.text, margin:0 }}>Live Stream Hub</h1>
              <p style={{ fontSize:13, color:C.muted, margin:'6px 0 0' }}>
                ERUE Festival 2026 · December 24–30 · Jattu-Uzairue, Edo State
              </p>
            </div>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              {channels.filter(c => c.isEnabled).map(c => (
                <div key={c.id} style={{ display:'flex', alignItems:'center', gap:6,
                  padding:'6px 12px', borderRadius:8,
                  background: c.isLive ? 'rgba(239,68,68,0.15)' : 'rgba(237,232,220,0.05)',
                  border:`1px solid ${c.isLive ? 'rgba(239,68,68,0.4)' : C.border}` }}>
                  <div style={{ width:6, height:6, borderRadius:'50%',
                    background: c.isLive ? C.red : C.muted }} />
                  <span style={{ fontSize:11, fontWeight:600,
                    color: c.isLive ? C.red : C.muted }}>
                    {c.platform === 'youtube' ? 'YouTube' : 'Facebook'}
                    {c.isLive ? ' LIVE' : ''}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Flash ───────────────────────────────────────────────────────── */}
        {msg && (
          <div style={{ padding:'10px 16px', borderRadius:8, marginBottom:16,
            background: msg.type==='ok' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
            border:`1px solid ${msg.type==='ok' ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
            color: msg.type==='ok' ? '#22c55e' : C.red, fontSize:13,
            display:'flex', alignItems:'center', gap:8 }}>
            {msg.type==='ok' ? <CheckCircle size={14}/> : <AlertCircle size={14}/>}
            {msg.text}
          </div>
        )}

        {/* ── Tabs ─────────────────────────────────────────────────────────── */}
        <div style={{ display:'flex', gap:4, marginBottom:24,
          borderBottom:`1px solid ${C.border}` }}>
          {([
            { id:'watch',    label:'📺 Watch Live' },
            { id:'schedule', label:'📅 Broadcast Schedule' },
            ...(isAdmin ? [{ id:'admin', label:'⚙️ Admin Controls' }] : []),
          ] as { id:string; label:string }[]).map(t => (
            <button key={t.id} onClick={() => setTab(t.id as any)}
              style={{ padding:'10px 18px', background:'none', border:'none',
                borderBottom:`2px solid ${tab===t.id ? C.gold : 'transparent'}`,
                color: tab===t.id ? C.gold : C.muted,
                fontSize:13, fontWeight:600, cursor:'pointer' }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ══════════════════════════════════════════════════════════════════
            WATCH TAB
        ══════════════════════════════════════════════════════════════════ */}
        {tab === 'watch' && (
          <div>
            {/* Channel selector */}
            <div style={{ display:'flex', gap:10, marginBottom:20, flexWrap:'wrap' }}>
              {channels.filter(c => c.isEnabled).map(c => (
                <button key={c.id} onClick={() => setActiveChannel(c.id)}
                  style={{ flex:1, minWidth:200, padding:'14px 18px',
                    background: activeChannel===c.id ? C.green : C.card,
                    border:`1px solid ${activeChannel===c.id ? C.gold : C.border}`,
                    borderRadius:10, cursor:'pointer', textAlign:'left' as const }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <div style={{ fontSize:24 }}>{c.platform==='youtube' ? '▶️' : '📘'}</div>
                    <div>
                      <div style={{ fontSize:13, fontWeight:600, color:C.text,
                        display:'flex', alignItems:'center', gap:8 }}>
                        {c.label}
                        {c.isLive && (
                          <span style={{ fontSize:9, fontWeight:700, padding:'2px 7px',
                            background:C.red, borderRadius:20, color:'#fff', letterSpacing:1 }}>
                            LIVE
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>
                        {c.scheduledDate}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Player */}
            {ch && (
              <div style={{ background:C.card, border:`1px solid ${C.border}`,
                borderRadius:14, overflow:'hidden', marginBottom:24 }}>
                {ch.embedId && ch.isEnabled ? (
                  ch.platform === 'youtube' ? (
                    <div style={{ position:'relative', paddingBottom:'56.25%', height:0 }}>
                      <iframe
                        src={ytEmbedUrl(ch.embedId)}
                        style={{ position:'absolute', top:0, left:0,
                          width:'100%', height:'100%', border:'none' }}
                        allowFullScreen allow="autoplay; encrypted-media"
                        title={ch.label}
                      />
                    </div>
                  ) : (
                    <div style={{ position:'relative', paddingBottom:'56.25%', height:0 }}>
                      <iframe
                        src={`https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(ch.embedId)}&show_text=false&autoplay=false`}
                        style={{ position:'absolute', top:0, left:0,
                          width:'100%', height:'100%', border:'none' }}
                        allowFullScreen title={ch.label}
                      />
                    </div>
                  )
                ) : (
                  /* ── Offline / Not-yet-active state ───────────────── */
                  <div style={{ background:'#050A07', minHeight:360, display:'flex',
                    flexDirection:'column', alignItems:'center', justifyContent:'center',
                    gap:16, padding:40 }}>
                    <div style={{ width:80, height:80, borderRadius:'50%',
                      background:'rgba(237,232,220,0.04)', border:`2px solid ${C.border}`,
                      display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <WifiOff size={32} style={{ color:C.muted }} />
                    </div>
                    <div style={{ textAlign:'center' }}>
                      <p style={{ fontSize:16, fontWeight:600, color:C.muted, marginBottom:6 }}>
                        {ch.isEnabled ? 'Stream Not Yet Active' : 'Channel Disabled'}
                      </p>
                      <p style={{ fontSize:13, color:C.dim, maxWidth:420, lineHeight:1.7 }}>
                        {ch.description}
                      </p>
                      <div style={{ marginTop:16, padding:'10px 20px',
                        background:C.surface, borderRadius:10,
                        border:`1px solid ${C.border}`, display:'inline-block' }}>
                        <div style={{ fontSize:11, color:C.gold, fontWeight:600 }}>
                          📅 {ch.scheduledDate}
                        </div>
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:10, flexWrap:'wrap', justifyContent:'center' }}>
                      <a href="https://www.youtube.com/@ERUEFestivalofUzairueKingdom"
                        target="_blank" rel="noreferrer"
                        style={{ display:'flex', alignItems:'center', gap:6, padding:'9px 18px',
                          background:'rgba(255,0,0,0.12)', border:'1px solid rgba(255,0,0,0.3)',
                          borderRadius:8, color:'#ff4444', fontSize:12, fontWeight:600,
                          textDecoration:'none' }}>
                        ▶️ Subscribe on YouTube
                      </a>
                      <a href="https://www.facebook.com/profile.php?id=61595036454360"
                        target="_blank" rel="noreferrer"
                        style={{ display:'flex', alignItems:'center', gap:6, padding:'9px 18px',
                          background:'rgba(59,130,246,0.12)', border:'1px solid rgba(59,130,246,0.3)',
                          borderRadius:8, color:C.blue, fontSize:12, fontWeight:600,
                          textDecoration:'none' }}>
                        📘 Follow on Facebook
                      </a>
                    </div>
                  </div>
                )}

                <div style={{ padding:'16px 20px', borderTop:`1px solid ${C.border}` }}>
                  <h3 style={{ fontFamily:'Cinzel,serif', fontSize:16, color:C.text,
                    margin:'0 0 4px' }}>{ch.label}</h3>
                  <p style={{ fontSize:13, color:C.muted, margin:0 }}>{ch.description}</p>
                </div>
              </div>
            )}

            {/* Social follow links */}
            <div style={{ background:C.card, border:`1px solid ${C.border}`,
              borderRadius:12, padding:'18px 20px' }}>
              <div style={{ fontSize:12, color:C.gold, fontWeight:700,
                textTransform:'uppercase', letterSpacing:1, marginBottom:12 }}>
                Follow ERUE Festival
              </div>
              <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                {[
                  { label:'YouTube',   url:'https://www.youtube.com/@ERUEFestivalofUzairueKingdom', color:'#ff4444', icon:'▶️' },
                  { label:'Facebook',  url:'https://www.facebook.com/profile.php?id=61595036454360', color:C.blue, icon:'📘' },
                  { label:'Instagram', url:'https://www.instagram.com/erueuzairue', color:'#e1306c', icon:'📸' },
                  { label:'X / Twitter', url:'https://x.com/erueuzairue', color:C.muted, icon:'🐦' },
                ].map(s => (
                  <a key={s.label} href={s.url} target="_blank" rel="noreferrer"
                    style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 14px',
                      background:`${s.color}15`, border:`1px solid ${s.color}30`,
                      borderRadius:8, color:s.color, fontSize:12, fontWeight:600,
                      textDecoration:'none' }}>
                    {s.icon} {s.label}
                  </a>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            SCHEDULE TAB
        ══════════════════════════════════════════════════════════════════ */}
        {tab === 'schedule' && (
          <div>
            <div style={{ display:'flex', justifyContent:'space-between',
              alignItems:'center', marginBottom:16 }}>
              <h2 style={{ fontFamily:'Cinzel,serif', fontSize:18, color:C.text, margin:0 }}>
                Broadcast Schedule — Dec 24–30, 2026
              </h2>
              {isAdmin && (
                <button onClick={() => setShowSlotForm(!showSlotForm)}
                  style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 14px',
                    background:C.green, border:`1px solid rgba(201,168,76,0.3)`,
                    borderRadius:8, color:C.gold, fontSize:12, fontWeight:600, cursor:'pointer' }}>
                  <Plus size={13}/> Add Slot
                </button>
              )}
            </div>

            {/* Add slot form */}
            {isAdmin && showSlotForm && (
              <div style={{ background:C.card, border:`1px solid ${C.border}`,
                borderRadius:12, padding:20, marginBottom:20 }}>
                <h3 style={{ color:C.gold, fontSize:13, marginBottom:14, margin:'0 0 14px' }}>
                  Add Broadcast Slot
                </h3>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                  {[
                    { label:'Date',    key:'date',  placeholder:'e.g. Dec 24' },
                    { label:'Time',    key:'time',  placeholder:'e.g. 10:00 AM' },
                    { label:'Title *', key:'title', placeholder:'e.g. Grand Opening Ceremony' },
                    { label:'Venue',   key:'venue', placeholder:'e.g. Palace of the Ogieneni' },
                  ].map(f => (
                    <div key={f.key}>
                      <label style={{ display:'block', fontSize:10, fontWeight:700,
                        letterSpacing:1, textTransform:'uppercase', color:C.muted, marginBottom:4 }}>
                        {f.label}
                      </label>
                      <input value={(slotForm as any)[f.key]}
                        onChange={e => setSlotForm(p => ({ ...p, [f.key]:e.target.value }))}
                        placeholder={f.placeholder} style={inp}/>
                    </div>
                  ))}
                  <div>
                    <label style={{ display:'block', fontSize:10, fontWeight:700,
                      letterSpacing:1, textTransform:'uppercase', color:C.muted, marginBottom:4 }}>
                      Type
                    </label>
                    <select value={slotForm.type}
                      onChange={e => setSlotForm(p => ({ ...p, type:e.target.value }))}
                      style={{ ...inp, cursor:'pointer' }}>
                      {['ceremony','cultural','pageant','sport','heritage','community'].map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ display:'block', fontSize:10, fontWeight:700,
                      letterSpacing:1, textTransform:'uppercase', color:C.muted, marginBottom:4 }}>
                      Channel
                    </label>
                    <select value={slotForm.streamChannelId}
                      onChange={e => setSlotForm(p => ({ ...p, streamChannelId:e.target.value }))}
                      style={{ ...inp, cursor:'pointer' }}>
                      {channels.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ display:'flex', gap:8, marginTop:14 }}>
                  <button onClick={addSlot} disabled={saving}
                    style={{ padding:'8px 18px', background:C.green,
                      border:`1px solid rgba(201,168,76,0.4)`, borderRadius:8,
                      color:C.gold, fontSize:12, fontWeight:700,
                      cursor:saving ? 'wait' : 'pointer', opacity:saving ? 0.6 : 1 }}>
                    {saving ? 'Saving…' : 'Add Slot'}
                  </button>
                  <button onClick={() => setShowSlotForm(false)}
                    style={{ padding:'8px 14px', background:'none',
                      border:`1px solid ${C.border}`, borderRadius:8,
                      color:C.muted, fontSize:12, cursor:'pointer' }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Schedule list */}
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {schedule.map(slot => {
                const slotCh = channels.find(c => c.id === slot.streamChannelId);
                return (
                  <div key={slot.id} style={{
                    background: slot.isLive ? 'rgba(239,68,68,0.06)' : C.card,
                    border:`1px solid ${slot.isLive ? 'rgba(239,68,68,0.4)' : C.border}`,
                    borderRadius:12, padding:'14px 18px',
                    display:'flex', alignItems:'center', gap:16, flexWrap:'wrap',
                  }}>
                    {/* Live pulse dot */}
                    {slot.isLive && (
                      <div style={{ width:8, height:8, borderRadius:'50%',
                        background:C.red, flexShrink:0,
                        boxShadow:'0 0 6px #ef4444',
                        animation:'blink 1.5s infinite' }} />
                    )}

                    {/* Date / Time */}
                    <div style={{ width:64, textAlign:'center' as const, flexShrink:0 }}>
                      <div style={{ fontSize:10, fontWeight:700, color:C.gold }}>{slot.date}</div>
                      <div style={{ fontSize:11, color:C.muted }}>{slot.time}</div>
                    </div>

                    {/* Title + venue */}
                    <div style={{ flex:1, minWidth:200 }}>
                      <div style={{ fontSize:14, fontWeight:600, color:C.text }}>
                        {slot.title}
                        {slot.isLive && (
                          <span style={{ marginLeft:8, fontSize:9, fontWeight:700,
                            padding:'2px 7px', background:C.red, borderRadius:20,
                            color:'#fff', letterSpacing:1 }}>LIVE</span>
                        )}
                      </div>
                      <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>
                        📍 {slot.venue} · via {slotCh?.label || slot.streamChannelId}
                      </div>
                    </div>

                    {/* Type badge */}
                    <div style={{ padding:'3px 10px', borderRadius:20, fontSize:10,
                      fontWeight:700, textTransform:'capitalize' as const,
                      background: TYPE_COLORS[slot.type] || 'rgba(201,168,76,0.1)',
                      color:C.gold, border:`1px solid rgba(201,168,76,0.2)` }}>
                      {slot.type}
                    </div>

                    {/* Admin controls */}
                    {isAdmin && (
                      <div style={{ display:'flex', gap:6 }}>
                        <button onClick={() => toggleSlotLive(slot.id)}
                          style={{ padding:'5px 12px', fontWeight:700, fontSize:10,
                            background: slot.isLive ? 'rgba(239,68,68,0.2)' : 'none',
                            border:`1px solid ${slot.isLive ? 'rgba(239,68,68,0.4)' : C.border}`,
                            borderRadius:6, color:slot.isLive ? C.red : C.muted, cursor:'pointer' }}>
                          {slot.isLive ? '◼ End' : '▶ Go Live'}
                        </button>
                        <button onClick={() => removeSlot(slot.id)}
                          style={{ padding:'5px 8px', background:'none',
                            border:`1px solid rgba(239,68,68,0.2)`, borderRadius:6,
                            color:C.red, fontSize:10, cursor:'pointer' }}>
                          <Trash2 size={11}/>
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            ADMIN TAB
        ══════════════════════════════════════════════════════════════════ */}
        {tab === 'admin' && isAdmin && (
          <div>
            <h2 style={{ fontFamily:'Cinzel,serif', fontSize:18, color:C.text, marginBottom:20 }}>
              Broadcast Control Panel
            </h2>

            <div style={{ display:'grid',
              gridTemplateColumns:'repeat(auto-fill,minmax(400px,1fr))', gap:16 }}>
              {channels.map(ch => (
                <div key={ch.id} style={{ background:C.card,
                  border:`1px solid ${C.border}`, borderRadius:12, padding:20 }}>

                  {/* Channel header */}
                  <div style={{ display:'flex', alignItems:'center',
                    justifyContent:'space-between', marginBottom:14 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <div style={{ fontSize:24 }}>{ch.platform==='youtube' ? '▶️' : '📘'}</div>
                      <div>
                        <div style={{ fontSize:14, fontWeight:700, color:C.text }}>{ch.label}</div>
                        <div style={{ fontSize:10, color:C.muted, textTransform:'uppercase',
                          letterSpacing:1 }}>{ch.platform}</div>
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:6 }}>
                      <button onClick={() => toggleLive(ch.id)}
                        style={{ padding:'6px 14px', fontWeight:700, fontSize:11,
                          background: ch.isLive ? 'rgba(239,68,68,0.2)' : C.green,
                          border:`1px solid ${ch.isLive ? 'rgba(239,68,68,0.4)' : 'rgba(201,168,76,0.4)'}`,
                          borderRadius:7, color:ch.isLive ? C.red : C.gold, cursor:'pointer' }}>
                        {ch.isLive ? '◼ End Stream' : '▶ Go Live'}
                      </button>
                      <button onClick={() => setEditChannel({ ...ch })}
                        style={{ padding:'6px 10px', background:'none',
                          border:`1px solid ${C.border}`, borderRadius:7,
                          color:C.muted, cursor:'pointer' }}>
                        <Edit2 size={13}/>
                      </button>
                      <button onClick={() => toggleEnabled(ch.id)}
                        style={{ padding:'6px 10px', background:'none',
                          border:`1px solid ${C.border}`, borderRadius:7,
                          color:ch.isEnabled ? '#22c55e' : C.muted, cursor:'pointer' }}>
                        {ch.isEnabled ? <Eye size={13}/> : <EyeOff size={13}/>}
                      </button>
                    </div>
                  </div>

                  {/* Status indicator */}
                  <div style={{ display:'flex', alignItems:'center', gap:10,
                    padding:'10px 14px', background:C.bg, borderRadius:8,
                    border:`1px solid ${C.border}`, marginBottom:10 }}>
                    <div style={{ width:8, height:8, borderRadius:'50%', flexShrink:0,
                      background: ch.isLive ? C.red : ch.isEnabled ? '#22c55e' : C.muted,
                      boxShadow: ch.isLive ? '0 0 6px #ef4444' : 'none' }} />
                    <div style={{ fontSize:12, color:C.muted }}>
                      {ch.isLive ? '🔴 LIVE NOW' : ch.isEnabled ? '⏳ Scheduled' : '⛔ Disabled'}
                    </div>
                  </div>

                  {/* Stream ID */}
                  <div style={{ fontSize:12, color:C.muted, marginBottom:6 }}>
                    <strong style={{ color:C.dim }}>Stream ID / URL: </strong>
                    <span style={{ color: ch.embedId ? C.text : C.red }}>
                      {ch.embedId || '⚠️ Not configured — click Edit to set'}
                    </span>
                  </div>
                  <div style={{ fontSize:11, color:C.dim }}>{ch.description}</div>
                </div>
              ))}
            </div>

            {/* ── Edit Channel Modal ───────────────────────────────────────── */}
            {editChannel && (
              <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)',
                zIndex:1000, display:'flex', alignItems:'center',
                justifyContent:'center', padding:20 }}>
                <div style={{ background:'#0E1812', border:`1px solid ${C.border}`,
                  borderRadius:16, padding:28, width:'100%', maxWidth:520 }}>
                  <div style={{ display:'flex', justifyContent:'space-between',
                    alignItems:'center', marginBottom:20 }}>
                    <h3 style={{ fontFamily:'Cinzel,serif', fontSize:16, color:C.gold, margin:0 }}>
                      Edit Stream Channel
                    </h3>
                    <button onClick={() => setEditChannel(null)}
                      style={{ background:'none', border:'none', color:C.muted,
                        fontSize:22, cursor:'pointer', lineHeight:1 }}>×</button>
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
                    {[
                      { label:'Channel Label', key:'label',
                        placeholder:'e.g. Main Stage — YouTube Live' },
                      { label:'Stream ID / YouTube Video ID / Facebook URL', key:'embedId',
                        placeholder:'YouTube: VIDEO_ID or Facebook: full URL' },
                      { label:'Scheduled Date', key:'scheduledDate',
                        placeholder:'e.g. December 24, 2026' },
                      { label:'Description', key:'description',
                        placeholder:'Brief description' },
                    ].map(f => (
                      <div key={f.key}>
                        <label style={{ display:'block', fontSize:10, fontWeight:700,
                          letterSpacing:1, textTransform:'uppercase',
                          color:C.muted, marginBottom:4 }}>
                          {f.label}
                        </label>
                        <input value={(editChannel as any)[f.key]}
                          onChange={e => setEditChannel(p => p ? { ...p, [f.key]:e.target.value } : p)}
                          placeholder={f.placeholder} style={inp}/>
                      </div>
                    ))}
                    {editChannel.platform === 'youtube' && (
                      <div style={{ padding:'10px 14px', background:C.bg,
                        borderRadius:8, border:`1px solid ${C.border}`,
                        fontSize:11, color:C.muted }}>
                        ℹ️ Paste the YouTube VIDEO_ID (e.g. <strong style={{color:C.text}}>dQw4w9WgXcQ</strong>)
                        or the full watch URL.
                      </div>
                    )}
                  </div>
                  <div style={{ display:'flex', gap:10, marginTop:20 }}>
                    <button onClick={saveEditChannel} disabled={saving}
                      style={{ flex:1, padding:'10px 20px', background:C.green,
                        border:`1px solid rgba(201,168,76,0.4)`, borderRadius:8,
                        color:C.gold, fontSize:13, fontWeight:700,
                        cursor:saving ? 'wait' : 'pointer', opacity:saving ? 0.6 : 1 }}>
                      {saving ? 'Saving…' : '✅ Save Channel'}
                    </button>
                    <button onClick={() => setEditChannel(null)}
                      style={{ padding:'10px 16px', background:'transparent',
                        border:`1px solid ${C.border}`, borderRadius:8,
                        color:C.muted, fontSize:13, cursor:'pointer' }}>
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.5; transform: scale(0.95); }
        }
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.3; }
        }
      `}</style>
    </div>
  );
};

export default LiveStreamHub;
